import { FieldError } from "hooks/use-errors.hook";

import { Interval } from "../availability-timeline.util";

// Overlap predicate is half-open (touching edges don't count), matching the backend's
// AppointmentQuerySet.filter_overlapping — the modal must classify a row exactly as the
// validator that raised the error did.
export function selectConflicts<T extends Interval>(
  rows: T[],
  attemptedStart: Date,
  attemptedEnd: Date
): T[] {
  return rows.filter(
    (row) => row.start < attemptedEnd && row.end > attemptedStart
  );
}

export function totalOverlapMinutes(
  rows: Interval[],
  attemptedStart: Date,
  attemptedEnd: Date
): number {
  return selectConflicts(rows, attemptedStart, attemptedEnd).reduce(
    (total, row) => {
      const overlapStart = Math.max(
        row.start.getTime(),
        attemptedStart.getTime()
      );
      const overlapEnd = Math.min(row.end.getTime(), attemptedEnd.getTime());
      return total + Math.round((overlapEnd - overlapStart) / 60000);
    },
    0
  );
}

// Two backend validations raise `staff_member_unavailable`: the model's overlap check
// ("...(busy)", scheduling/models/appointment.py) and the serializer's no-schedule check
// ("...(not working)", scheduling/serializers/appointment.py). Only the busy variant is a
// provider conflict — a bare code match would open the modal on a no-schedule save.
// Don't reword either backend message without updating this predicate.
export function isStaffBusyError(error: FieldError): boolean {
  return (
    error.code === "staff_member_unavailable" &&
    error.message.endsWith("(busy)")
  );
}

export function hasStaffBusyError(
  errors: { nonFieldErrors: FieldError[] } | undefined | null
): boolean {
  return Boolean(errors?.nonFieldErrors?.some(isStaffBusyError));
}
